import { useEffect, useRef, useState } from "react";
import { PVP } from "@zend/content";
import { useSession } from "../state/session.js";
import type { BattleReport } from "../units/types.js";
import { requestScope } from "../api/scope.js";
import { navigate } from "../router.js";
import { useT } from "../i18n/index.js";
import { Button } from "../ui/Button.js";
import { Icon } from "../ui/Icon.js";
import { Timer, useServerTick } from "../ui/Timer.js";
import { worldApi } from "./api.js";
import { useWorld } from "./state.js";
import "./revenge.css";

/** Opens target intel and squad selection; preparing revenge never starts or pays for an attack. */
export function RevengeButton({ battleId, expiresAt, onPrepared }: { battleId: string; expiresAt?: number | null; onPrepared?: () => void }) {
  const t = useT(), now = useServerTick();
  const [availableUntil, setAvailableUntil] = useState<number | null>(expiresAt ?? null);
  const [busy, setBusy] = useState(false), lock = useRef(false);
  useEffect(() => {
    if (expiresAt !== undefined) { setAvailableUntil(expiresAt); return; }
    let alive = true; const generation = requestScope().generation;
    void worldApi.revenge(battleId).then(preview => { if (alive && generation === requestScope().generation) setAvailableUntil(preview?.expiresAt ?? null); }).catch(() => { if (alive) setAvailableUntil(null); });
    return () => { alive = false; };
  }, [battleId, expiresAt]);
  if (!availableUntil || availableUntil <= now) return null;
  const prepare = async () => {
    if (lock.current) return;
    lock.current = true; setBusy(true);
    try { if (await useWorld.getState().prepareRevenge(battleId)) { onPrepared?.(); navigate("/app/map"); } }
    finally { lock.current = false; setBusy(false); }
  };
  return <div className="revenge-action" data-testid="revenge-action">
    <Button variant="gold" loading={busy} onClick={() => void prepare()} data-testid="revenge-prepare"><Icon name="attack" /> {t("world.revengePrepare")}</Button>
    <span>+{PVP.revengePct}% {t("world.damage")} · <Timer finishesAt={availableUntil} /></span>
  </div>;
}

export function ReportRevenge({ report, onPrepared }: { report: BattleReport | null; onPrepared: () => void }) {
  const account = useSession(s => s.account);
  if (!report || report.context !== "base_attack" || !account || report.accountId === account.id) return null;
  return <RevengeButton key={report.id} battleId={report.id} onPrepared={onPrepared} />;
}
